import { logger } from '@magicschool/logger';
import { createUnlockedAudioElement, loadAndPlayAudioBlob, releaseAudioElement } from '@/features/tts/preserveAudioUserGesture';
import { shouldUseBlobTtsPlayback } from '../utils/shouldUseBlobTtsPlayback';
import type { TTSOptions, TTSProvider } from './types';

interface CacheEntry {
  audioUrl: string;
  opts: TTSOptions;
  element: HTMLAudioElement | null;
  isPaused: boolean;
  isReady: boolean;
}

export class StreamingTTSProvider implements TTSProvider {
  name = 'streaming-backend';
  private cache = new Map<string, CacheEntry>();
  private currentKey: string | null = null;
  private onEnd: (() => void) | null = null;
  private onProgressCallback: ((progress: number, duration: number | null) => void) | null = null;
  private onReadyCallback: (() => void) | null = null;
  private fetchCtrl: AbortController | null = null;
  private settings: TTSOptions;
  private maxEntries: number;
  private mediaElements: HTMLAudioElement[] = [];
  private sourceBufferOperationQueue: Map<SourceBuffer, Promise<void>> = new Map();
  private isOperationInProgress = false;

  constructor(
    private endpoint: string,
    defaults: TTSOptions = {},
    maxCacheEntries = 10,
  ) {
    this.settings = { ...defaults };
    this.maxEntries = maxCacheEntries;
  }

  isReady = (): boolean => {
    if (!this.currentKey) return false;
    const entry = this.cache.get(this.currentKey);
    return !!entry?.isReady;
  };

  /** Blob + HTMLAudioElement playback works in all modern browsers; avoid Web Speech fallback. */
  isSupported = (): boolean => typeof window !== 'undefined';

  setVolume = (v: number): void => {
    this.settings.volume = v;

    if (this.currentKey) {
      const entry = this.cache.get(this.currentKey);
      if (entry?.element) {
        entry.element.volume = v;
      }
    }

    for (const entry of this.cache.values()) {
      if (entry.element) {
        entry.element.volume = v;
      }
    }
  };

  setRate = (r: number): void => {
    this.settings.rate = r;

    if (this.currentKey) {
      const entry = this.cache.get(this.currentKey);
      if (entry?.element) {
        entry.element.playbackRate = r;
      }
    }

    for (const entry of this.cache.values()) {
      if (entry.element) {
        entry.element.playbackRate = r;
      }
    }
  };

  setLang = (lang: string): void => {
    this.settings.lang = lang;
  };

  setVoice = (voice: string): void => {
    this.settings.voice = voice;
  };

  setOnEnded = (cb: () => void): void => {
    this.onEnd = cb;
  };

  onProgress = (callback: (progress: number, duration: number | null) => void): void => {
    this.onProgressCallback = callback;
  };

  onReady = (callback: () => void): void => {
    this.onReadyCallback = callback;
  };

  getCurrentTime = (): number => {
    if (this.currentKey) {
      const entry = this.cache.get(this.currentKey);
      if (entry?.element) {
        return entry.element.currentTime;
      }
    }
    return 0;
  };

  getDuration = (): number | null => {
    if (this.currentKey) {
      const entry = this.cache.get(this.currentKey);
      if (entry?.element && !isNaN(entry.element.duration)) {
        return entry.element.duration;
      }
    }
    return null;
  };

  clearCache = (): void => {
    this.stop();

    for (const entry of this.cache.values()) {
      this.releaseCacheEntryElement(entry);
    }

    this.cache.clear();
    this.currentKey = null;
    this.mediaElements = [];

    this.abortRequest();
  };

  getSettings = (): TTSOptions => {
    return { ...this.settings };
  };

  abortRequest = (): void => {
    if (this.fetchCtrl) {
      this.fetchCtrl.abort('Aborted by user');
      this.fetchCtrl = null;
    }

    if (this.currentKey) {
      const entry = this.cache.get(this.currentKey);
      if (entry?.element) {
        // Close any open MediaSource before releasing the element
        if (entry.element.srcObject instanceof MediaSource) {
          try {
            this.safelyEndMediaSource(entry.element.srcObject);
          } catch (e) {
            logger.error('Error closing MediaSource during abort:', e);
          }
        }

        this.releaseCacheEntryElement(entry);
        this.cache.delete(this.currentKey);
      }
    }

    this.currentKey = null;
  };

  private makeKey = (text: string, opts?: TTSOptions): string => {
    return JSON.stringify({
      text,
      lang: opts?.lang !== undefined ? opts.lang : this.settings.lang,
      voice: opts?.voice !== undefined ? opts.voice : this.settings.voice,
      rate: 1,
    });
  };

  private buildRequestBody(text: string, opts?: TTSOptions): string {
    return JSON.stringify({
      text,
      ...(opts?.lang ? { lang: opts.lang } : {}),
      ...(opts?.voice ? { voice: opts.voice } : {}),
      speed: 1,
    });
  }

  private attachAudioElementListeners(key: string, audioElement: HTMLAudioElement): void {
    audioElement.addEventListener('timeupdate', () => {
      if (this.onProgressCallback) {
        this.onProgressCallback(audioElement.currentTime, !isNaN(audioElement.duration) ? audioElement.duration : null);
      }
    });

    audioElement.addEventListener('canplay', () => {
      if (this.onReadyCallback) {
        this.onReadyCallback();
      }
    });

    audioElement.addEventListener('ended', () => {
      this.handleAudioEnded(key);
    });
  }

  private async fetchBlobAudio(
    text: string,
    opts: TTSOptions | undefined,
    key: string,
    ctrl: AbortController,
    gestureAudio: HTMLAudioElement,
  ): Promise<HTMLAudioElement> {
    const body = this.buildRequestBody(text, opts);
    const audioElement = gestureAudio;
    audioElement.volume = opts?.volume ?? this.settings.volume ?? 1;

    let response: Response;
    try {
      response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: ctrl.signal,
        body,
        responseErrorHandlers: {
          unknown: () => ({ shortCircuit: true }),
        },
      });

      if (!response.ok) {
        throw new Error(`TTS fetch failed: ${response.status}`);
      }
    } catch (err) {
      if (typeof err === 'string' && err === 'Aborted by user') {
        throw err;
      }

      const error = err as Error;
      logger.error('Network error during TTS fetch:', error);
      throw new Error(`Network error during TTS fetch: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    const blob = await response.blob();

    this.attachAudioElementListeners(key, audioElement);
    this.mediaElements.push(audioElement);
    this.fetchCtrl = null;

    this.cache.set(key, {
      audioUrl: '',
      opts: opts ?? {},
      element: audioElement,
      isPaused: false,
      isReady: false,
    });

    this.pruneCache();

    try {
      await loadAndPlayAudioBlob(audioElement, blob);

      const entry = this.cache.get(key);
      if (entry) {
        entry.audioUrl = audioElement.src;
        entry.isReady = true;
      }

      if (this.onReadyCallback) {
        this.onReadyCallback();
      }
    } catch (error) {
      logger.error('Error playing blob audio:', error);
      throw error;
    }

    return audioElement;
  }

  private async streamAudio(text: string, opts?: TTSOptions, gestureAudio?: HTMLAudioElement | null): Promise<HTMLAudioElement> {
    const key = this.makeKey(text, opts);

    if (this.cache.has(key)) {
      const entry = this.cache.get(key);
      const cachedSrc = entry?.audioUrl || entry?.element?.src;
      if (cachedSrc) {
        if (!entry.element) {
          entry.element = new Audio(cachedSrc);

          // Set up event listeners for the audio element
          entry.element.addEventListener('ended', () => {
            this.handleAudioEnded(key);
          });

          // Add event listeners for progress tracking
          entry.element.addEventListener('timeupdate', () => {
            if (this.onProgressCallback && entry.element) {
              this.onProgressCallback(entry.element.currentTime, !isNaN(entry.element.duration) ? entry.element.duration : null);
            }
          });

          entry.element.addEventListener('canplay', () => {
            if (this.onReadyCallback) {
              this.onReadyCallback();
            }
          });

          this.mediaElements.push(entry.element);
        }
        return entry.element;
      }
    }

    this.fetchCtrl?.abort();
    const ctrl = new AbortController();
    this.fetchCtrl = ctrl;

    if (shouldUseBlobTtsPlayback()) {
      if (!gestureAudio) {
        throw new Error('Gesture-bound audio element required for blob TTS playback');
      }
      return this.fetchBlobAudio(text, opts, key, ctrl, gestureAudio);
    }

    const body = this.buildRequestBody(text, opts);

    const audioElement = new Audio();
    audioElement.volume = opts?.volume ?? this.settings.volume ?? 1;
    this.attachAudioElementListeners(key, audioElement);

    // Create a MediaSource instance
    const mediaSource = new MediaSource();
    const audioUrl = URL.createObjectURL(mediaSource);
    audioElement.src = audioUrl;

    this.mediaElements.push(audioElement);

    // Set up event handlers for streaming
    await new Promise((resolveSetup, rejectSetup) => {
      const setupTimeout = setTimeout(() => {
        rejectSetup(new Error('MediaSource setup timed out'));
      }, 10000); // 10 second timeout

      mediaSource.addEventListener('sourceopen', async () => {
        clearTimeout(setupTimeout);
        resolveSetup(true);

        try {
          const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
          this.sourceBufferOperationQueue.set(sourceBuffer, Promise.resolve());

          let response: Response | undefined;
          try {
            response = await fetch(this.endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              signal: ctrl.signal,
              body,
              responseErrorHandlers: {
                unknown: () => {
                  return { shortCircuit: true };
                },
              },
            });

            if (!response.ok) {
              throw new Error(`TTS fetch failed: ${response.status}`);
            }
          } catch (err) {
            if (typeof err === 'string' && err === 'Aborted by user') {
              return;
            }

            const error = err as Error;
            logger.error('Network error during TTS fetch:', error);
            if (error instanceof Error) {
              throw new Error(`Network error during TTS fetch: ${error.message}`);
            } else {
              throw new Error('Network error during TTS fetch: Unknown error');
            }
          }

          if (!response.body) {
            throw new Error('Response body is not readable');
          }

          const reader = response.body.getReader();
          let isFirstChunk = true;

          const processChunks = async () => {
            try {
              while (true) {
                const { done, value } = await reader.read();

                if (done) {
                  try {
                    await this.safelyEndMediaSource(mediaSource, sourceBuffer);
                  } catch (e) {
                    logger.error('Error ending media stream:', e);
                  }
                  break;
                }

                try {
                  await this.queueSourceBufferOperation(sourceBuffer, async () => {
                    sourceBuffer.appendBuffer(value);

                    // Wait for the operation to complete
                    await new Promise<void>((resolve) => {
                      const updateEndHandler = () => {
                        sourceBuffer.removeEventListener('updateend', updateEndHandler);
                        resolve();
                      };

                      if (sourceBuffer.updating) {
                        sourceBuffer.addEventListener('updateend', updateEndHandler);
                      } else {
                        resolve();
                      }
                    });
                  });

                  if (isFirstChunk) {
                    isFirstChunk = false;
                    setTimeout(() => {
                      const entry = this.cache.get(key);
                      if (entry) {
                        entry.isReady = true;
                      }

                      if (this.onReadyCallback) {
                        this.onReadyCallback();
                      }
                    }, 100);
                  }
                } catch (e) {
                  const err = e as Error;
                  logger.error(`Error appending buffer: ${err.message}`, e);

                  if (err instanceof Error && err.name === 'QuotaExceededError') {
                    await new Promise((resolve) => {
                      const timeUpdateHandler = () => {
                        if (
                          sourceBuffer.buffered &&
                          sourceBuffer.buffered.length > 0 &&
                          audioElement.currentTime > sourceBuffer.buffered.start(0) + 2
                        ) {
                          audioElement.removeEventListener('timeupdate', timeUpdateHandler);
                          resolve(null);
                        }
                      };
                      audioElement.addEventListener('timeupdate', timeUpdateHandler);
                    });
                  } else if (err instanceof Error && err.name !== 'AbortError') {
                    logger.warn(`Recoverable error in appendBuffer: ${err.message}`);
                  }
                }
              }
            } catch (e) {
              if (e instanceof Error && e.name !== 'AbortError') {
                logger.error(`Streaming chunk processing error with message: ${e.message}`, e);
                // Don't rethrow - we want to continue in case of recoverable errors
              }
            }
          };

          const processingPromise = processChunks();

          const safetyTimeout = setTimeout(() => {
            if (isFirstChunk) {
              isFirstChunk = false;
              logger.warn('TTS stream taking longer than expected to start, forcing ready state');

              const entry = this.cache.get(key);
              if (entry) {
                entry.isReady = true;
              }

              if (this.onReadyCallback) {
                this.onReadyCallback();
              }
            }
          }, 5000);

          processingPromise.finally(() => {
            clearTimeout(safetyTimeout);
          });

          this.fetchCtrl = null;

          // Cache the audio
          this.cache.set(key, {
            audioUrl,
            opts: opts ?? {},
            element: audioElement,
            isPaused: false,
            isReady: false,
          });

          this.pruneCache();
        } catch (error) {
          if (error instanceof Error && error.name !== 'AbortError') {
            logger.error('Streaming error:', error);
          }

          URL.revokeObjectURL(audioUrl);
          if (this.currentKey === key) {
            this.currentKey = null;
          }

          const index = this.mediaElements.indexOf(audioElement);
          if (index > -1) {
            this.mediaElements.splice(index, 1);
          }

          throw error;
        }
      });

      mediaSource.addEventListener('error', (event) => {
        clearTimeout(setupTimeout);
        rejectSetup(new Error(`MediaSource error: ${event.toString()}`));
      });
    });

    return audioElement;
  }

  private handleAudioEnded(key: string): void {
    const entry = this.cache.get(key);
    if (entry) {
      entry.isPaused = false;
    }

    if (this.currentKey === key) {
      this.currentKey = null;
      this.onEnd?.();
    }
  }

  play = async (text: string, opts?: TTSOptions): Promise<void> => {
    const useBlob = shouldUseBlobTtsPlayback();
    const volumeToUse = opts?.volume ?? this.settings.volume ?? 1;
    const gestureAudio = opts?.gestureAudio ?? (useBlob ? createUnlockedAudioElement(volumeToUse) : null);

    const key = this.makeKey(text, opts);
    const entry = this.cache.get(key);

    // If this is a different audio than what's currently playing,
    // stop the current audio first
    if (this.currentKey && this.currentKey !== key) {
      this.stop();
    }

    // If paused entry exists for the same audio, just resume
    if (entry?.isPaused && entry.element) {
      this.currentKey = key;
      await this.resume();
      return;
    }

    // Set current key before streaming
    this.currentKey = key;

    try {
      const audioElement = await this.streamAudio(
        text,
        {
          ...this.getSettings(),
          ...opts,
        },
        gestureAudio,
      );

      audioElement.volume = volumeToUse;

      const rateToUse = opts?.rate ?? this.settings.rate ?? 1;
      audioElement.playbackRate = rateToUse;

      // Blob path starts playback inside fetchBlobAudio; desktop streaming still needs play()
      if (audioElement.paused) {
        try {
          await audioElement.play();
        } catch (e) {
          logger.error('Error playing audio:', e);
          if (e instanceof Error && e.name === 'NotAllowedError') {
            logger.warn('Audio playback requires user interaction first');
          }
          throw e;
        }
      }
    } catch (error) {
      logger.error('Error in play method:', error);
      this.currentKey = null;
      throw error;
    }
  };

  pause = (): void => {
    if (this.isOperationInProgress) {
      logger.warn('Operation in progress, deferring pause');
      return;
    }

    if (!this.currentKey) return;
    const entry = this.cache.get(this.currentKey);
    if (!entry || !entry.element) return;

    this.isOperationInProgress = true;
    try {
      entry.element.pause();
      entry.isPaused = true;
    } catch (e) {
      logger.error('Error pausing audio:', e);
    } finally {
      this.isOperationInProgress = false;
    }
  };

  resume = async (): Promise<void> => {
    if (this.isOperationInProgress) {
      logger.warn('Operation in progress, deferring resume');
      return;
    }

    if (!this.currentKey) return;
    const entry = this.cache.get(this.currentKey);
    if (!entry || !entry.element || !entry.isPaused) return;

    this.isOperationInProgress = true;
    try {
      await entry.element.play();
      entry.isPaused = false;
    } catch (e) {
      logger.error('Error resuming audio:', e);
    } finally {
      this.isOperationInProgress = false;
    }
  };

  stop = (): void => {
    if (this.isOperationInProgress) {
      logger.warn('Operation in progress, deferring stop');
      return;
    }

    if (!this.currentKey) return;
    const entry = this.cache.get(this.currentKey);
    if (entry?.element) {
      this.isOperationInProgress = true;
      try {
        entry.element.pause();
        entry.element.currentTime = 0;
        entry.isPaused = false;
      } catch (e) {
        logger.error('Error stopping audio:', e);
      } finally {
        this.isOperationInProgress = false;
      }
    }
    this.currentKey = null;
  };

  reset = (): void => {
    if (this.isOperationInProgress) {
      logger.warn('Operation in progress, deferring reset');
      return;
    }

    if (!this.currentKey) return;
    const entry = this.cache.get(this.currentKey);
    if (!entry || !entry.element) return;

    this.isOperationInProgress = true;
    try {
      entry.element.pause();
      entry.element.currentTime = 0;
      entry.isPaused = false;
    } catch (e) {
      logger.error('Error resetting audio:', e);
    } finally {
      this.isOperationInProgress = false;
    }
  };

  isPlaying = (text?: string): boolean => {
    if (!this.currentKey) return false;
    const entry = this.cache.get(this.currentKey);
    if (!entry || !entry.element) return false;

    if (text) {
      return !entry.element.paused && !entry.isPaused && this.makeKey(text, entry.opts) === this.currentKey;
    }

    return !entry.element.paused && !entry.isPaused;
  };

  private pruneCache = (): void => {
    if (this.cache.size <= this.maxEntries) {
      return;
    }

    const entries: [string, CacheEntry][] = [];
    for (const [entryKey, entry] of this.cache.entries()) {
      if (this.currentKey !== entryKey) {
        entries.push([entryKey, entry]);
      }
    }

    while (entries.length > 0 && this.cache.size > this.maxEntries) {
      const entryPair = entries.shift();
      if (!entryPair) {
        break;
      }

      const [keyToRemove, entryToRemove] = entryPair;

      this.releaseCacheEntryElement(entryToRemove);

      // Remove from cache
      this.cache.delete(keyToRemove);
    }
  };

  private releaseCacheEntryElement(entry: CacheEntry): void {
    if (entry.element) {
      const index = this.mediaElements.indexOf(entry.element);
      if (index > -1) {
        this.mediaElements.splice(index, 1);
      }

      if (entry.audioUrl.startsWith('blob:') && entry.audioUrl !== entry.element.src) {
        URL.revokeObjectURL(entry.audioUrl);
      }

      releaseAudioElement(entry.element);
      entry.element = null;
      entry.audioUrl = '';
      return;
    }

    if (entry.audioUrl.startsWith('blob:')) {
      URL.revokeObjectURL(entry.audioUrl);
      entry.audioUrl = '';
    }
  }

  /**
   * Safely queue a source buffer operation to ensure only one operation happens at a time
   */
  private queueSourceBufferOperation = async (sourceBuffer: SourceBuffer, operation: () => Promise<void> | void): Promise<void> => {
    const currentPromise = this.sourceBufferOperationQueue.get(sourceBuffer) || Promise.resolve();
    const newPromise = currentPromise
      .then(async () => {
        if (sourceBuffer.updating) {
          await new Promise<void>((resolve) => {
            const updateEndHandler = () => {
              sourceBuffer.removeEventListener('updateend', updateEndHandler);
              resolve();
            };
            sourceBuffer.addEventListener('updateend', updateEndHandler);
          });
        }

        return operation();
      })
      .catch((error) => {
        logger.error('Error in source buffer operation:', error);
      });

    this.sourceBufferOperationQueue.set(sourceBuffer, newPromise);
    return newPromise;
  };

  /**
   * Safely end a media source, ensuring all source buffer operations are complete
   */
  private safelyEndMediaSource = async (mediaSource: MediaSource, sourceBuffer?: SourceBuffer): Promise<void> => {
    try {
      if (sourceBuffer) {
        await this.queueSourceBufferOperation(sourceBuffer, async () => {
          // No operation needed, just ensuring the queue is clear
        });
      } else if (mediaSource.sourceBuffers?.length > 0) {
        await Promise.all(
          Array.from(mediaSource.sourceBuffers).map((sb) =>
            this.queueSourceBufferOperation(sb, async () => {
              // No operation needed, just ensuring the queue is clear
            }),
          ),
        );
      }

      // Only end the stream if it's still open
      if (mediaSource.readyState === 'open') {
        mediaSource.endOfStream();
      }
    } catch (e) {
      logger.error('Error safely ending media source:', e);
      throw e;
    }
  };
}
